import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CommuneField from "@/components/CommuneField";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, btnOutline, overline, inputCls, labelCls } from "@/components/kit";
import { ArrowRight, ArrowUpRight, Check, CheckCircle2, Paperclip } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { CONTACT } from "@/data/site";

const FAQ = [
  { q: "Quel budget prévoir pour une vitrine de Noël ?", a: "Une vitrine décorée démarre à 1 490 € HT. Le budget dépend des dimensions, du niveau de scénographie et du nombre de points de vente." },
  { q: "L'installation peut-elle se faire hors horaires d'ouverture ?", a: "Oui : nous installons tôt le matin, le soir ou le week-end pour ne jamais perturber votre activité." },
  { q: "Le démontage est-il inclus ?", a: "Oui, le démontage et la remise en état sont planifiés en janvier, aux dates convenues avec vous." },
  { q: "Comment obtenir une proposition pour mon magasin ?", a: "Remplissez le formulaire ci-dessus avec les dimensions de votre vitrine et, si possible, une photo : première réponse sous 24 h ouvrées." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Décoration de Noël pour magasins et vitrines",
  description: "Décoration de Noël professionnelle pour magasins : vitrine, vitrophanie, sapin, scénographie, installation hors horaires d'ouverture et démontage en janvier.",
  provider: {
    "@type": "LocalBusiness",
    name: "Désir Events",
    url: "https://desir-events.com",
    telephone: CONTACT.phoneFixe,
    email: CONTACT.email,
    address: { "@type": "PostalAddress", streetAddress: "129 rue de Servient", addressLocality: "Lyon", postalCode: "69003", addressCountry: "FR" },
  },
  areaServed: { "@type": "Country", name: "France" },
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
    { "@type": "ListItem", position: 2, name: "Noël en entreprise" },
    { "@type": "ListItem", position: 3, name: "Décoration Noël magasin", item: "https://desir-events.com/decoration-noel-magasin" },
  ],
};

const PRESTATIONS = [
  "Décoration de vitrine", "Vitrophanie de Noël", "Sapin et mise en lumière",
  "Scénographie intérieure", "Arches et décors d'entrée", "Coin photo de Noël",
  "Chalet et espace Père Noël", "Décoration suspendue", "Mise en scène des produits",
  "Conception d'un décor multisite", "Installation en dehors des horaires d'ouverture",
  "Démontage et remise en état en janvier",
];

const OFFRES = [
  { name: "Vitrine Essentielle", price: "dès 1 490 € HT", note: "Conception, maquette, vitrophanie ou décor léger, installation et démontage." },
  { name: "Boutique Signature", price: "dès 2 900 € HT", note: "Vitrine, sapin, éclairage, scénographie intérieure et mise en scène des produits." },
  { name: "Noël Immersif", price: "dès 5 900 € HT", note: "Décor complet, espace photo, éléments volumineux, éclairage et coordination générale." },
  { name: "Réseaux de boutiques", price: "sur devis", note: "Concept commun, fabrication, transport et installation sur plusieurs sites." },
];

const DEROULE = [
  { step: "Photos & mesures", text: "Envoyez des photos de votre vitrine et ses dimensions : nous concevons à partir de l'existant." },
  { step: "Maquette", text: "Une proposition visuelle avant toute fabrication — vous validez le décor avant production." },
  { step: "Installation", text: "Pose en dehors des horaires d'ouverture, nuit ou dimanche si nécessaire : zéro impact sur votre activité." },
  { step: "Démontage", text: "En janvier, nous démontons et remettons en état. Le décor peut être stocké pour l'année suivante." },
];

const NB_MAGASINS = ["1 magasin", "2 à 5", "6 à 20", "Plus de 20"];
const ZONES = ["Intérieure", "Extérieure", "Les deux"];
const BUDGETS = ["Moins de 2 000 €", "2 000 à 5 000 €", "5 000 à 10 000 €", "Plus de 10 000 €", "À définir"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="group">
      {options.map((o) => {
        const active = value === o;
        return (
          <button type="button" key={o} aria-pressed={active} onClick={() => onChange(o)}
            data-testid={`${testid}-${o.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function DecoMagasinForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", magasins: "", dimensions: "",
    city: "", city_code: "", install: "", demontage: "", zone: "", budget: "", message: "", brief_file_url: "",
  });
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Fichier trop lourd (15 Mo max)."); return; }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/upload/brief", data);
      set({ brief_file_url: res.url });
      toast.success("Photo de la vitrine jointe.");
    } catch { toast.error("Échec de l'envoi de la photo."); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Votre nom est requis."); return; }
    if (!EMAIL_RE.test(form.email)) { toast.error("Un email valide est requis."); return; }
    if (!form.phone.trim()) { toast.error("Un téléphone est requis."); return; }
    setLoading(true);
    try {
      const details = [
        form.magasins && `Nombre de magasins : ${form.magasins}`,
        form.dimensions && `Dimensions vitrine (L x H) : ${form.dimensions}`,
        form.zone && `Décoration : ${form.zone}`,
        form.demontage && `Démontage souhaité : ${form.demontage}`,
        form.message,
      ].filter(Boolean).join("\n");
      const { data } = await api.post("/submissions", {
        type: "devis", service: "Décoration de Noël — magasin",
        name: form.name, company: form.company, email: form.email, phone: form.phone,
        city: form.city, city_code: form.city_code, event_date: form.install,
        budget: form.budget, message: details, brief_file_url: form.brief_file_url,
        source: getSource(),
      });
      track("form_submit");
      setSent(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-lime-dim/40 bg-lime/10 p-8 text-center" data-testid="deco-magasin-success">
        <CheckCircle2 size={34} className="mx-auto text-lime-dim" />
        <p className="mt-3 font-serif text-2xl text-ink">Demande bien reçue{sent.dossier ? ` — dossier ${sent.dossier}` : ""}.</p>
        <p className="mt-2 text-sm text-ink/60">Un chef de projet étudie vos photos et dimensions, puis vous rappelle sous 24 h ouvrées.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-ink/10 bg-white p-8 md:p-10" data-testid="deco-magasin-form">
      <p className="font-serif text-2xl text-ink">Votre décor de Noël, sur mesure</p>
      <p className="mt-2 text-sm text-ink/55">Joignez une photo de votre vitrine : nous concevons à partir de l'existant.</p>
      <div className="mt-7 space-y-7">
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="deco-name" className={labelCls}>Votre nom <span className="text-lime-dim">*</span></label>
            <input id="deco-name" type="text" required className={inputCls} data-testid="deco-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="deco-company" className={labelCls}>Boutique / réseau</label>
            <input id="deco-company" type="text" className={inputCls} data-testid="deco-company" value={form.company} placeholder="Nom du magasin ou de l'enseigne" onChange={(e) => set({ company: e.target.value })} />
          </div>
          <div>
            <label htmlFor="deco-email" className={labelCls}>Email <span className="text-lime-dim">*</span></label>
            <input id="deco-email" type="email" required className={inputCls} data-testid="deco-email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
          <div>
            <label htmlFor="deco-phone" className={labelCls}>Téléphone <span className="text-lime-dim">*</span></label>
            <input id="deco-phone" type="tel" required className={inputCls} data-testid="deco-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </div>
        </div>
        <div>
          <p className={labelCls}>Nombre de magasins à décorer</p>
          <Pills options={NB_MAGASINS} value={form.magasins} onChange={(v) => set({ magasins: v })} testid="deco-magasins" />
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="deco-city" className={labelCls}>Ville</label>
            <CommuneField id="deco-city" placeholder="Commune du ou des magasins…" onSelect={(v) => set(v)} />
          </div>
          <div>
            <label htmlFor="deco-dimensions" className={labelCls}>Dimensions approximatives de la vitrine</label>
            <input id="deco-dimensions" type="text" className={inputCls} data-testid="deco-dimensions" value={form.dimensions} placeholder="Ex : 4 m x 2,5 m" onChange={(e) => set({ dimensions: e.target.value })} />
          </div>
          <div>
            <label htmlFor="deco-install" className={labelCls}>Date d'installation souhaitée</label>
            <input id="deco-install" type="date" className={inputCls} data-testid="deco-install" value={form.install} onChange={(e) => set({ install: e.target.value })} />
          </div>
          <div>
            <label htmlFor="deco-demontage" className={labelCls}>Date de démontage souhaitée</label>
            <input id="deco-demontage" type="date" className={inputCls} data-testid="deco-demontage" value={form.demontage} onChange={(e) => set({ demontage: e.target.value })} />
          </div>
        </div>
        <div>
          <p className={labelCls}>Décoration</p>
          <Pills options={ZONES} value={form.zone} onChange={(v) => set({ zone: v })} testid="deco-zone" />
        </div>
        <div>
          <p className={labelCls}>Budget envisagé</p>
          <Pills options={BUDGETS} value={form.budget} onChange={(v) => set({ budget: v })} testid="deco-budget" />
        </div>
        <div>
          <p className={labelCls}>Photo de la vitrine (recommandé)</p>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-ink/20 bg-white px-5 py-4 text-sm text-ink/60 transition-colors hover:border-lime-dim" data-testid="deco-photo-drop">
            <Paperclip size={16} className="text-lime-dim" />
            {uploading ? "Envoi en cours…" : form.brief_file_url ? "Photo jointe ✓ — cliquez pour la remplacer" : "Joindre une photo (15 Mo max)"}
            <input type="file" className="hidden" accept="image/*,.pdf" data-testid="deco-photo-input" onChange={uploadPhoto} disabled={uploading} />
          </label>
        </div>
        <div>
          <label htmlFor="deco-message" className={labelCls}>Précisions (optionnel)</label>
          <textarea id="deco-message" rows={3} className={inputCls} data-testid="deco-message" value={form.message} placeholder="Style souhaité, contraintes d'accès, horaires…" onChange={(e) => set({ message: e.target.value })} />
        </div>
      </div>
      <button type="submit" disabled={loading} onClick={() => track("click_devis")} className={`${btnPrimary} mt-8 w-full justify-center disabled:opacity-60`} data-testid="deco-submit">
        {loading ? "Envoi…" : "Recevoir ma maquette et mon devis"} <ArrowRight size={16} />
      </button>
      <p className="mt-3 text-center text-xs text-ink/45">Réponse sous 24 h ouvrées · sans engagement</p>
    </form>
  );
}

export default function DecorationNoelMagasin() {
  return (
    <>
      <Seo
        title="Décoration Noël magasin | Vitrines, boutiques et centres commerciaux"
        description="Décoration de Noël professionnelle pour magasins : vitrine, vitrophanie, sapin, scénographie, installation hors horaires et démontage en janvier. Vitrine dès 1 490 € HT."
        path="/decoration-noel-magasin"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SERVICE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_JSONLD)}</script>
      </Helmet>
      <PageHero eyebrow="Noël · Retail" title={<>Décoration de Noël professionnelle<br /><span className="italic text-lime-dim">pour magasins et vitrines</span></>} />

      {/* Prestations */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="deco-magasin-prestations">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-ink/70">
            Une vitrine de Noël réussie se mesure en clients qui s'arrêtent. Conception, maquette, fabrication, installation et démontage : une seule équipe coordonne tout, y compris en multisite pour les réseaux de boutiques.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PRESTATIONS.map((p) => (
            <div key={p} className="flex items-start gap-3 rounded-xl border border-ink/10 bg-mist px-5 py-4">
              <Check size={15} className="mt-0.5 shrink-0 text-lime-dim" />
              <p className="text-sm font-medium text-ink">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Offres */}
      <section className="border-t border-ink/10 bg-white" data-testid="deco-magasin-offres">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Nos formules</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Des prix de départ affichés, un devis détaillé</h2></Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {OFFRES.map((o, i) => (
              <Reveal key={o.name} delay={i}>
                <div className={`h-full rounded-2xl border p-7 ${i === 1 ? "border-lime-dim/50 bg-mist shadow-lg" : "border-ink/10 bg-white"}`} data-testid={`deco-offre-${i}`}>
                  <p className="font-serif text-xl text-ink">{o.name}</p>
                  <p className="mt-3 font-serif text-2xl text-lime-dim">{o.price}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{o.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}><p className="mt-6 max-w-2xl text-sm text-ink/50">Prix de départ HT. Le devis final dépend des dimensions, des matériaux et du nombre de sites — il détaille toujours conception, fabrication, transport, pose et démontage.</p></Reveal>
        </div>
      </section>

      {/* Déroulé */}
      <section className="border-t border-ink/10 bg-mist" data-testid="deco-magasin-deroule">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>De la maquette à janvier</span></Reveal>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-ink/10 md:grid-cols-4">
            {DEROULE.map((d, i) => (
              <Reveal key={d.step} delay={i}>
                <div className="h-full bg-white p-7">
                  <span className="font-serif text-3xl text-lime-dim">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-xl text-ink">{d.step}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire dédié */}
      <section className="border-t border-ink/10 bg-ink text-white" data-testid="deco-magasin-form-section">
        <div className="mx-auto grid max-w-[1440px] items-start gap-12 px-5 py-16 md:grid-cols-2 md:px-10 md:py-24">
          <div>
            <Reveal><span className={`${overline} !text-lime`}>Votre vitrine de Noël</span></Reveal>
            <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">Envoyez une photo,<br />recevez une maquette.</h2></Reveal>
            <Reveal delay={2}><p className="mt-5 max-w-md text-base leading-relaxed text-white/60">Quelques informations et une photo suffisent : notre équipe conçoit une proposition adaptée à vos dimensions, votre enseigne et votre budget.</p></Reveal>
          </div>
          <Reveal delay={1}><DecoMagasinForm /></Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-mist" data-testid="deco-magasin-faq">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7" data-testid={`deco-magasin-faq-${i}`}>
                  <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="border-t border-ink/10 bg-white" data-testid="deco-magasin-related">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>Autour de Noël</span></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { label: "Décoration de Noël en entreprise", to: "/decoration-noel-entreprise" },
              { label: "Soirée de Noël d'entreprise à Lyon", to: "/fete-de-noel-entreprise/lyon" },
              { label: "Animations de Noël", to: "/animation-noel-entreprise" },
              { label: "Arbre de Noël CSE à Lyon", to: "/evenements-cse/lyon" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-mist px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`deco-related-${r.to.replace(/\//g, "")}`}>
                {r.label} <ArrowUpRight size={14} className="text-lime-dim" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 pb-20 md:px-10" data-testid="deco-magasin-call">
        <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline}>Une question ? {CONTACT.phoneMobile}</a>
      </section>
    </>
  );
}
